import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import './Dashboard.css';
import { capitalizeName } from '../lib/format';
import MultiSelectDropdown from './MultiSelectDropdown';
import { LOSS_TYPES, ESTIMATING_SOFTWARE, FLORIDA_COUNTIES } from '../lib/profileOptions';
import './SurveyModal.css';
import NotificationBell from './NotificationBell';

// ─── STAR DISPLAY ────────────────────────────────────────────────────────────
const Stars = ({ rating = 0, size = 14 }) => {
  const stars = [1, 2, 3, 4, 5];
  return (
    <div className="profile-rating-stars">
      {stars.map(s => {
        const full     = s <= Math.floor(rating);
        const partial  = !full && s === Math.ceil(rating);
        const pct      = partial ? Math.round((rating % 1) * 100) : 0;
        return (
          <svg key={s} width={size} height={size} viewBox="0 0 20 20">
            {partial && (
              <defs>
                <linearGradient id={`g-${s}`} x1="0" x2="1">
                  <stop offset={`${pct}%`} stopColor="var(--gold)" />
                  <stop offset={`${pct}%`} stopColor="var(--border)" />
                </linearGradient>
              </defs>
            )}
            <polygon
              points="10,1 12.9,7 19.5,7.6 14.5,12 16.2,18.5 10,15 3.8,18.5 5.5,12 0.5,7.6 7.1,7"
              fill={full ? 'var(--gold)' : partial ? `url(#g-${s})` : 'var(--border)'}
              stroke="var(--gold)"
              strokeWidth="0.5"
            />
          </svg>
        );
      })}
    </div>
  );
};

// ─── PREMIUM LOCKED FIELD ────────────────────────────────────────────────────
const LockedField = ({ label, placeholder }) => {
  const [showMsg, setShowMsg] = useState(false);

  const handleClick = () => {
    setShowMsg(true);
    setTimeout(() => setShowMsg(false), 5000);
  };

  return (
    <div className="edit-field edit-field-locked" onClick={handleClick}>
      <div className="edit-label">
        {label}
        <span className="edit-label-premium">Premium</span>
      </div>
      <input
        className="edit-input-locked"
        type="text"
        placeholder={placeholder}
        readOnly
        tabIndex={-1}
      />
      {showMsg && (
        <div className="premium-upgrade-label">
          Premium feature — Upgrade to unlock
        </div>
      )}
    </div>
  );
};

// ─── TOGGLE ──────────────────────────────────────────────────────────────────
const Toggle = ({ checked, onChange }) => (
  <label className="toggle">
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
    <span className="toggle-slider" />
  </label>
);

// ─── PROFILE TAB ─────────────────────────────────────────────────────────────
const ProfileTab = ({ profile, licenses, isPremium, onSave, userId }) => {
  const [form, setForm] = useState({
    display_name:    profile?.display_name    || '',
    bio:             profile?.bio             || '',
    years_experience: profile?.years_experience || '',
    certifications:  (profile?.certifications || []).join(', '),
    loss_types:       profile?.loss_types || [],
    estimating_software: profile?.estimating_software || [],
    conflict_of_interest_policy: profile?.conflict_of_interest_policy || false,
    travel_counties:  profile?.travel_counties || [],
    travel_statewide: profile?.travel_statewide || false,
    virtual_appraisals: profile?.virtual_appraisals || false,
    avg_cycle_time_days: profile?.avg_cycle_time_days || '',
    specialties: (profile?.specialties || []).join(', '),
    conflict_of_interest_policy: profile?.conflict_of_interest_policy || false,
    virtual_appraisals: profile?.virtual_appraisals || false,
    travel_counties: profile?.travel_counties || [],
    travel_statewide: profile?.travel_statewide || false,
  });
  const [saving,            setSaving]            = useState(false);
  const [saved,             setSaved]             = useState(false);
  const [uploadingHeadshot, setUploadingHeadshot] = useState(false);
  const [headshotUrl,       setHeadshotUrl]       = useState(profile?.headshot_url || null);
  const fileInputRef = React.useRef(null);

  const handleHeadshotUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !userId) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) { alert('Please upload a JPG, PNG, or WebP image.'); return; }
    if (file.size > 2 * 1024 * 1024) { alert('Image must be under 2 MB.'); return; }
    setUploadingHeadshot(true);
    try {
      const path = `${userId}/avatar`;
      const { error: uploadErr } = await supabase.storage
        .from('avatars')
        .upload(path, file, { upsert: true, contentType: file.type });
      if (uploadErr) throw uploadErr;
      const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(path);
      const url = `${publicUrl}?t=${Date.now()}`;
      await supabase.from('profiles').update({ headshot_url: url }).eq('user_id', userId);
      setHeadshotUrl(url);
    } catch (err) {
      alert('Upload failed: ' + (err.message || 'Unknown error'));
    } finally {
      setUploadingHeadshot(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave({
      display_name:     form.display_name,
      bio:              form.bio,
      years_experience: form.years_experience ? parseInt(form.years_experience) : null,
      certifications:   form.certifications.split(',').map(s => s.trim()).filter(Boolean),
      loss_types:       form.loss_types,
      estimating_software: form.estimating_software,
      conflict_of_interest_policy: form.conflict_of_interest_policy,
      travel_counties:  form.travel_statewide ? [] : form.travel_counties,
      travel_statewide: form.travel_statewide,
      virtual_appraisals: form.virtual_appraisals,
      avg_cycle_time_days: form.avg_cycle_time_days ? parseInt(form.avg_cycle_time_days) : null,
    });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="dashboard-section-body">

      <div className="edit-field">
        <label className="edit-label">Display Name</label>
        <input
          className="edit-input"
          type="text"
          placeholder="Professional alias (optional) — e.g. Richard ''Rick'' Smith"
          value={form.display_name}
          onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
        />
      </div>

      <div className="edit-field">
        <label className="edit-label">Bio</label>
        <textarea
          className="edit-textarea"
          placeholder="Brief professional background..."
          value={form.bio}
          onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
          maxLength={500}
        />
        <div style={{ fontSize: '11px', color: 'var(--muted)', textAlign: 'right' }}>
          {form.bio.length}/500
        </div>
      </div>

      <div className="edit-field">
        <label className="edit-label">Certifications</label>
        <input
          className="edit-input"
          type="text"
          placeholder="WIND, IAUA, CPUC, SCLA, Etc (comma separated)"
          value={form.certifications}
          onChange={e => setForm(f => ({ ...f, certifications: e.target.value }))}
        />
      </div>

      <MultiSelectDropdown
        label="Loss Type / Perils"
        options={LOSS_TYPES}
        selected={form.loss_types}
        onChange={v => setForm(f => ({ ...f, loss_types: v }))}
        placeholder="Select perils you handle..."
      />

      <MultiSelectDropdown
        label="Estimating Software"
        options={ESTIMATING_SOFTWARE}
        selected={form.estimating_software}
        onChange={v => setForm(f => ({ ...f, estimating_software: v }))}
        placeholder="Select software you use..."
      />

      <div className="edit-field">
        <label className="edit-label">Avg Cycle Time (days)</label>
        <input
          className="edit-input"
          type="number"
          placeholder="e.g. 14"
          value={form.avg_cycle_time_days}
          onChange={e => setForm(f => ({ ...f, avg_cycle_time_days: e.target.value }))}
        />
        {profile?.avg_cycle_time_verified && (
          <div style={{ fontSize: '11px', color: 'var(--gold)', marginTop: '4px' }}>✓ System Verified</div>
        )}
      </div>

      {/* Headshot — premium only */}
      {isPremium ? (
        <div className="edit-field">
          <label className="edit-label">Headshot</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            {headshotUrl
              ? <img src={headshotUrl} alt="Headshot" style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--border)' }} onError={() => setHeadshotUrl(null)} />
              : <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--cream)', border: '2px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: 'var(--muted)', fontWeight: 600 }}>
                  {(profile?.display_name || 'U').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                </div>
            }
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                style={{ display: 'none' }}
                onChange={handleHeadshotUpload}
              />
              <button
                className="edit-save-btn"
                style={{ fontSize: '12px', padding: '6px 14px' }}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingHeadshot}
              >
                {uploadingHeadshot ? 'Uploading…' : headshotUrl ? 'Change photo' : 'Upload photo'}
              </button>
              <div style={{ fontSize: '11px', color: 'var(--muted)' }}>JPG, PNG or WebP · max 2 MB</div>
            </div>
          </div>
        </div>
      ) : (
        <LockedField label="Headshot" placeholder="Upload a professional photo" />
      )}

{isPremium ? (
  <div className="edit-field">
    <label className="edit-label">Specialties</label>
    <input
      className="edit-input"
      type="text"
      placeholder="Residential, Commercial, CAT..."
      value={form.specialties}
      onChange={e => setForm(f => ({ ...f, specialties: e.target.value }))}
    />
  </div>
) : (
  <LockedField label="Specialties" placeholder="Residential, Commercial, CAT..." />
)}

{isPremium ? (
  <div className="edit-field">
    <label className="edit-label">Years Experience</label>
    <input
      className="edit-input"
      type="number"
      placeholder="Years of appraisal experience"
      value={form.years_experience}
      onChange={e => setForm(f => ({ ...f, years_experience: e.target.value }))}
    />
  </div>
) : (
  <LockedField label="Years Experience" placeholder="Years of appraisal experience" />
)}

{isPremium ? (
  <>
    {!form.travel_statewide && (
      <MultiSelectDropdown
        label="Covered Counties"
        options={FLORIDA_COUNTIES}
        selected={form.travel_counties}
        onChange={v => setForm(f => ({ ...f, travel_counties: v }))}
        placeholder="Select counties you travel to..."
      />
    )}
  </>
) : (
  <LockedField label="Travel Policy" placeholder="Select counties you cover" />
)}

{isPremium ? (
  <div className="settings-row">
    <div className="settings-row-left">
      <div className="settings-row-label">Conflict of Interest Policy</div>
      <div className="settings-row-desc">Indicates you maintain a formal COI policy</div>
    </div>
    <Toggle checked={form.conflict_of_interest_policy} onChange={v => setForm(f => ({ ...f, conflict_of_interest_policy: v }))} />
  </div>
) : (
  <LockedField label="Conflict of Interest Policy" placeholder="Indicate your COI policy" />
)}

{isPremium ? (
  <div className="settings-row">
    <div className="settings-row-left">
      <div className="settings-row-label">Virtual Appraisals</div>
      <div className="settings-row-desc">You offer remote/virtual appraisal services</div>
    </div>
    <Toggle checked={form.virtual_appraisals} onChange={v => setForm(f => ({ ...f, virtual_appraisals: v }))} />
  </div>
) : (
  <LockedField label="Virtual Appraisals" placeholder="Offer remote appraisals" />
)}


      <div className="edit-save-row">
        <button className="edit-save-btn" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save Changes'}
        </button>
      </div>

    </div>
  );
};

// ─── ADD LICENSE MODAL ───────────────────────────────────────────────────────
const AddLicenseModal = ({ userId, onClose, onAdded }) => {
  const [form, setForm] = useState({
    license_type: '',
    license_type_detail: '',
    state: '',
    license_number: '',
    expiry_date: '',
    is_home: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const LICENSE_TYPES = ['PA', 'IA', 'Contractor', 'Attorney', 'Engineer', 'Other'];
  const US_STATES = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
    'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
    'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
    'VA','WA','WV','WI','WY','DC'
  ];

  const showTypeDetail = form.license_type === 'Contractor' || form.license_type === 'Other';

  const handleSave = async () => {
  if (!form.license_type) { setError('License type is required.'); return; }
  if (!form.state) { setError('State is required.'); return; }
  if (!form.license_number.trim()) { setError('License number is required.'); return; }
  if (form.expiry_date && new Date(form.expiry_date) < new Date()) {
    setError('License is expired. Only active licenses can be added.');
    return;
}
  setSaving(true);
  setError('');

  // Step 1 — insert license row, get back the new ID
  const { data: licenseData, error: err } = await supabase
    .from('licenses')
    .insert({
      user_id:             userId,
      license_type:        form.license_type,
      license_type_detail: showTypeDetail ? (form.license_type_detail.trim() || null) : null,
      state:               form.state,
      expiry_date:         form.expiry_date || null,
      is_home:             form.is_home,
      verification_method: 'unverified',
      verified:            false,
      license_status:      'active',
      license_category:    'appraisal',
    })
    .select('id')
    .single();

  if (err) {
    setError('Failed to save license: ' + err.message);
    setSaving(false);
    return;
  }

  // Step 2 — store license number encrypted
  console.log('[store_license_number] calling with:', { p_license_id: licenseData.id, p_license_number: form.license_number.trim() });
  const rpcResult = await supabase.rpc('store_license_number', {
    p_license_id:     licenseData.id,
    p_license_number: form.license_number.trim(),
  });
  console.log('[store_license_number] raw response:', JSON.stringify(rpcResult));

  if (rpcResult.error) {
    console.error('License number encryption failed:', rpcResult.error);
    // Non-blocking — license row exists, number just didn't encrypt
  }

  setSaving(false);
  onAdded();
  onClose();
};

  const handleOverlay = (e) => { if (e.target === e.currentTarget) onClose(); };

  return (
    <div className="modal-overlay" onClick={handleOverlay} style={{ zIndex: 1100 }}>
      <div className="modal" style={{ maxWidth: '440px' }}>
        <div className="modal-header" style={{ borderBottom: '1px solid var(--border)', paddingBottom: '16px' }}>
          <div className="modal-identity">
            <div className="modal-name" style={{ fontSize: '16px' }}>Add License or Credential</div>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          
          {error && (
            <div style={{ fontSize: '13px', color: '#dc2626', background: '#fee2e2', borderRadius: 'var(--radius-sm)', padding: '10px 12px' }}>
              {error}
            </div>
          )}

          <div className="edit-field">
            <label className="edit-label">License Type <span style={{ color: 'red' }}>*</span></label>
            <select
              className="edit-input"
              value={form.license_type}
              onChange={e => setForm(f => ({ ...f, license_type: e.target.value }))}
            >
              <option value="">Select type...</option>
              {LICENSE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {showTypeDetail && (
            <div className="edit-field">
              <label className="edit-label">
                Specify type <span style={{ fontSize: '11px', color: 'var(--muted)', fontWeight: 300 }}>(optional)</span>
              </label>
              <input
                className="edit-input"
                type="text"
                placeholder="e.g. Roofer, General Contractor, Mold Remediator"
                value={form.license_type_detail}
                onChange={e => setForm(f => ({ ...f, license_type_detail: e.target.value }))}
              />
            </div>
          )}

          <div className="edit-field">
            <label className="edit-label">State <span style={{ color: 'red' }}>*</span></label>
            <select
              className="edit-input"
              value={form.state}
              onChange={e => setForm(f => ({ ...f, state: e.target.value }))}
            >
              <option value="">Select state...</option>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div className="edit-field">
            <label className="edit-label">License Number <span style={{ fontSize: '11px', color: 'var(--muted)', fontWeight: 300 }}>(optional)</span></label>
            <input
              className="edit-input"
              type="text"
              placeholder="e.g. A123456"
              value={form.license_number}
              onChange={e => setForm(f => ({ ...f, license_number: e.target.value }))}
            />
          </div>

          <div className="edit-field">
            <label className="edit-label">Expiry Date <span style={{ fontSize: '11px', color: 'var(--muted)', fontWeight: 300 }}>(optional)</span></label>
            <input
              className="edit-input"
              type="date"
              value={form.expiry_date}
              onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))}
            />
          </div>

          <div className="settings-row">
            <div className="settings-row-left">
              <div className="settings-row-label">Home state license 🏠</div>
              <div className="settings-row-desc">Your primary state of residence and licensure</div>
            </div>
            <label className="toggle">
              <input
                type="checkbox"
                checked={form.is_home}
                onChange={e => setForm(f => ({ ...f, is_home: e.target.checked }))}
              />
              <span className="toggle-slider" />
            </label>
          </div>

          <div style={{ fontSize: '12px', color: 'var(--muted)', lineHeight: 1.5 }}>
            Manually added licenses are marked as <strong>Unverified</strong> until reviewed by our team. NIPR-verified licenses will be imported automatically once your NPN is confirmed.
          </div>

          <div className="edit-save-row">
            <button className="edit-save-btn" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Add License'}
            </button>
            <button
              className="edit-save-btn"
              style={{ marginLeft: '8px', background: 'transparent', color: 'var(--muted)' }}
              onClick={onClose}
            >
              Cancel
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

// ─── EDIT LICENSE MODAL ──────────────────────────────────────────────────────
const EditLicenseModal = ({ license, onClose, onSaved }) => {
  const LICENSE_TYPES = ['PA', 'IA', 'Contractor', 'Attorney', 'Engineer', 'Other'];
  const US_STATES = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
    'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
    'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
    'VA','WA','WV','WI','WY','DC'
  ];

  const [form, setForm] = useState({
    license_type:        license.license_type        || '',
    license_type_detail: license.license_type_detail || '',
    state:                license.state               || '',
    license_number:       '',
    expiry_date:          license.expiry_date   ? license.expiry_date.slice(0, 10) : '',
    is_home:              license.is_home       ?? false,
  });
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  // Pre-fill license number by decrypting the stored value
  useEffect(() => {
    supabase.rpc('get_license_number', { p_license_id: license.id })
      .single()
      .then(({ data, error: rpcErr }) => {
        if (!rpcErr && data) setForm(f => ({ ...f, license_number: data }));
      })
      .finally(() => setLoading(false));
  }, [license.id]);

  const showTypeDetail = form.license_type === 'Contractor' || form.license_type === 'Other';

  const handleSave = async () => {
    if (!form.license_type)         { setError('License type is required.'); return; }
    if (!form.state)                { setError('State is required.'); return; }
    if (!form.license_number.trim()) { setError('License number is required.'); return; }
    setSaving(true);
    setError('');

    // Update non-encrypted fields
    const { error: updateErr } = await supabase
      .from('licenses')
      .update({
        license_type:        form.license_type,
        license_type_detail: showTypeDetail ? (form.license_type_detail.trim() || null) : null,
        state:                form.state,
        expiry_date:          form.expiry_date || null,
        is_home:              form.is_home,
      })
      .eq('id', license.id);

    if (updateErr) {
      setError('Failed to update license: ' + updateErr.message);
      setSaving(false);
      return;
    }

    // Update encrypted license number
    const rpcResult = await supabase.rpc('store_license_number', {
      p_license_id:     license.id,
      p_license_number: form.license_number.trim(),
    });

    if (rpcResult.error) {
      setError('License saved but number encryption failed: ' + rpcResult.error.message);
      setSaving(false);
      return;
    }

    setSaving(false);
    onSaved();
    onClose();
  };

  const handleOverlay = (e) => { if (e.target === e.currentTarget) onClose(); };

  return (
    <div className="modal-overlay" onClick={handleOverlay} style={{ zIndex: 1100 }}>
      <div className="modal" style={{ maxWidth: '440px' }}>
        <div className="modal-header" style={{ borderBottom: '1px solid var(--border)', paddingBottom: '16px' }}>
          <div className="modal-identity">
            <div className="modal-name" style={{ fontSize: '16px' }}>Edit License</div>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {error && (
            <div style={{ fontSize: '13px', color: '#dc2626', background: '#fee2e2', borderRadius: 'var(--radius-sm)', padding: '10px 12px' }}>
              {error}
            </div>
          )}

          <div className="edit-field">
            <label className="edit-label">License Type <span style={{ color: 'red' }}>*</span></label>
            <select className="edit-input" value={form.license_type} onChange={e => setForm(f => ({ ...f, license_type: e.target.value }))}>
              <option value="">Select type...</option>
              {LICENSE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {showTypeDetail && (
            <div className="edit-field">
              <label className="edit-label">
                Specify type <span style={{ fontSize: '11px', color: 'var(--muted)', fontWeight: 300 }}>(optional)</span>
              </label>
              <input
                className="edit-input"
                type="text"
                placeholder="e.g. Roofer, General Contractor, Mold Remediator"
                value={form.license_type_detail}
                onChange={e => setForm(f => ({ ...f, license_type_detail: e.target.value }))}
              />
            </div>
          )}

          <div className="edit-field">
            <label className="edit-label">State <span style={{ color: 'red' }}>*</span></label>
            <select className="edit-input" value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))}>
              <option value="">Select state...</option>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div className="edit-field">
            <label className="edit-label">License Number <span style={{ color: 'red' }}>*</span></label>
            <input
              className="edit-input"
              type="text"
              placeholder={loading ? 'Loading…' : 'e.g. A123456'}
              value={form.license_number}
              disabled={loading}
              onChange={e => setForm(f => ({ ...f, license_number: e.target.value }))}
            />
          </div>

          <div className="edit-field">
            <label className="edit-label">Expiry Date <span style={{ fontSize: '11px', color: 'var(--muted)', fontWeight: 300 }}>(optional)</span></label>
            <input
              className="edit-input"
              type="date"
              value={form.expiry_date}
              onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))}
            />
          </div>

          <div className="settings-row">
            <div className="settings-row-left">
              <div className="settings-row-label">Home state license 🏠</div>
              <div className="settings-row-desc">Your primary state of residence and licensure</div>
            </div>
            <label className="toggle">
              <input type="checkbox" checked={form.is_home} onChange={e => setForm(f => ({ ...f, is_home: e.target.checked }))} />
              <span className="toggle-slider" />
            </label>
          </div>

          <div className="edit-save-row">
            <button className="edit-save-btn" onClick={handleSave} disabled={saving || loading}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button className="edit-save-btn" style={{ marginLeft: '8px', background: 'transparent', color: 'var(--muted)' }} onClick={onClose}>
              Cancel
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

// ─── LICENSES TAB ────────────────────────────────────────────────────────────
const LicensesTab = ({ licenses, userId, onRefresh }) => {
  const [showAddModal,   setShowAddModal]   = useState(false);
  const [editingLicense, setEditingLicense] = useState(null);
  const [deletingId,     setDeletingId]     = useState(null);
  const [licenseNumbers, setLicenseNumbers] = useState({});

  useEffect(() => {
    if (!licenses?.length) return;
    Promise.all(
      licenses.map(lic =>
        supabase.rpc('get_license_number', { p_license_id: lic.id })
          .single()
          .then(({ data }) => [lic.id, data])
      )
    ).then(results => {
      const map = {};
      results.forEach(([id, num]) => { if (num) map[id] = num; });
      setLicenseNumbers(map);
    });
  }, [licenses]);

  const deleteLicense = async (lic) => {
    if (!window.confirm(`Delete ${lic.license_type} — ${lic.state}? This cannot be undone.`)) return;
    setDeletingId(lic.id);
    const { error } = await supabase.from('licenses').delete().eq('id', lic.id);
    setDeletingId(null);
    if (error) { alert('Delete failed: ' + error.message); return; }
    onRefresh();
  };

  const sorted = [...(licenses || [])].sort((a, b) =>
    `${a.license_type} ${a.state}`.localeCompare(`${b.license_type} ${b.state}`)
  );

  const verificationLabel = (method, verified) => {
    if (verified && method === 'nipr')   return { label: 'NIPR Verified', cls: 'verified' };
    if (verified && method === 'manual') return { label: 'Verified',      cls: 'verified' };
    if (method === 'unverified')         return { label: 'Pending',       cls: 'pending' };
    return { label: 'Unverified', cls: 'unverified' };
  };

  return (
    <div className="dashboard-section-body">
      <div className="license-list">
        {sorted.length === 0 && (
          <p style={{ color: 'var(--muted)', fontSize: '14px', fontWeight: 300 }}>
            No licenses on file. Add your NPN from Settings to import your NIPR licenses.
          </p>
        )}
        {sorted.map(lic => {
          const v = verificationLabel(lic.verification_method, lic.verified);
          return (
            <div key={lic.id} className="license-item">
              <div className="license-item-left">
                <div className="license-item-title">
                  {lic.license_type}{lic.license_type_detail ? ` — ${lic.license_type_detail}` : ''} {lic.is_home ? '🏠' : ''}
                </div>
                <div className="license-item-detail">
                  {lic.state}
                  {licenseNumbers[lic.id] ? ` · #${licenseNumbers[lic.id]}` : ''}
                </div>
                <div className="license-item-detail" style={{ marginTop: '2px' }}>
                  {lic.expiry_date ? `Expires ${new Date(lic.expiry_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : 'No expiry on file'}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span className={`license-item-badge ${v.cls}`}>{v.label}</span>
                <button
                  onClick={() => setEditingLicense(lic)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: '14px', padding: '2px 4px', lineHeight: 1 }}
                  title="Edit license"
                >
                  ✏️
                </button>
                {!lic.is_home && (
                  <button
                    onClick={() => deleteLicense(lic)}
                    disabled={deletingId === lic.id}
                    style={{ background: 'none', border: 'none', cursor: deletingId === lic.id ? 'not-allowed' : 'pointer', color: '#dc2626', fontSize: '14px', padding: '2px 4px', lineHeight: 1, opacity: deletingId === lic.id ? 0.4 : 1 }}
                    title="Delete license"
                  >
                    🗑
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <button className="add-license-btn" onClick={() => setShowAddModal(true)}>
        + Add additional licenses
      </button>

      {showAddModal && (
        <AddLicenseModal
          userId={userId}
          onClose={() => setShowAddModal(false)}
          onAdded={onRefresh}
        />
      )}

      {editingLicense && (
        <EditLicenseModal
          license={editingLicense}
          onClose={() => setEditingLicense(null)}
          onSaved={() => { setEditingLicense(null); onRefresh(); }}
        />
      )}
    </div>
  );
};

// ─── TESTIMONIALS TAB ────────────────────────────────────────────────────────
const formatAuthorName = (displayName) => {
  if (!displayName) return 'Anonymous';
  const words = displayName.trim().split(/\s+/).filter(Boolean);
  if (words.length === 1) return words[0];
  const last = words[words.length - 1].replace(/\.$/, '');
  return `${words[0]} ${last[0].toUpperCase()}.`;
};

const TestimonialsTab = ({ profileId, isPremium }) => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [pinning,      setPinning]      = useState(null);
  const [replyingTo,   setReplyingTo]   = useState(null);
  const [replyText,    setReplyText]    = useState('');
  const [savingReply,  setSavingReply]  = useState(false);
  const [statFilter,   setStatFilter]   = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('testimonials')
      .select('id, content, attributed_side, author_user_id, created_at, is_pinned, owner_reply, owner_reply_at')
      .eq('target_profile_id', profileId)
      .eq('is_visible', true)
      .order('is_pinned', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(50);

    if (!data?.length) { setTestimonials([]); setLoading(false); return; }

    const authorIds = [...new Set(data.map(t => t.author_user_id))];
    const { data: authorProfiles } = await supabase
      .from('profiles')
      .select('user_id, display_name')
      .in('user_id', authorIds);

    const nameMap = {};
    authorProfiles?.forEach(p => { nameMap[p.user_id] = p.display_name; });

    setTestimonials(data.map(t => ({ ...t, author_name: formatAuthorName(nameMap[t.author_user_id]) })));
    setLoading(false);
  }, [profileId]);

  useEffect(() => { load(); }, [load]);

  const saveReply = async (t) => {
    setSavingReply(true);
    const { error } = await supabase
      .from('testimonials')
      .update({ owner_reply: replyText.trim() || null, owner_reply_at: replyText.trim() ? new Date().toISOString() : null })
      .eq('id', t.id);
    if (error) console.error('saveReply error:', error.message);
    setSavingReply(false);
    setReplyingTo(null);
    setReplyText('');
    load();
  };

  const togglePin = async (t) => {
    if (!isPremium || pinning) return;
    setPinning(t.id);
    if (t.is_pinned) {
      await supabase.from('testimonials').update({ is_pinned: false }).eq('id', t.id);
    } else {
      await supabase.from('testimonials').update({ is_pinned: false }).eq('target_profile_id', profileId);
      await supabase.from('testimonials').update({ is_pinned: true }).eq('id', t.id);
    }
    setPinning(null);
    load();
  };

  const cutoffDate = statFilter === 'all' ? null : (() => {
    const d = new Date();
    d.setDate(d.getDate() - parseInt(statFilter, 10));
    return d;
  })();

  const visible = cutoffDate
    ? testimonials.filter(t => new Date(t.created_at) >= cutoffDate)
    : testimonials;

  const stats = {
    total:        visible.length,
    carrier:      visible.filter(t => t.attributed_side === 'Carrier').length,
    policyholder: visible.filter(t => t.attributed_side === 'Policyholder').length,
  };

  if (loading) return <div className="dashboard-section-body" style={{ color: 'var(--muted)', fontSize: '14px' }}>Loading…</div>;

  return (
    <div className="dashboard-section-body">

      {/* Stats panel */}
      {testimonials.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
            {[['all', 'All time'], ['30', '30 days'], ['60', '60 days'], ['90', '90 days']].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setStatFilter(key)}
                style={{
                  padding: '4px 12px', fontSize: '12px', borderRadius: '99px',
                  border: `1.5px solid ${statFilter === key ? 'var(--navy)' : 'var(--muted)'}`,
                  background: statFilter === key ? 'var(--navy)' : 'transparent',
                  color: statFilter === key ? 'white' : 'var(--muted)',
                  fontFamily: 'var(--sans)', cursor: 'pointer',
                  fontWeight: statFilter === key ? 600 : 400, transition: 'all 0.15s',
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '16px' }}>
            {[
              { label: 'Total', value: stats.total },
              { label: 'Carrier (IA)', value: stats.carrier },
              { label: 'Policyholder (PA)', value: stats.policyholder },
            ].map(({ label, value }) => (
              <div key={label} style={{ background: 'var(--cream)', borderRadius: 'var(--radius-md)', padding: '14px 12px', textAlign: 'center', border: '1px solid rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize: '26px', fontWeight: 700, color: 'var(--navy)', lineHeight: 1 }}>{value}</div>
                <div style={{ fontSize: '10px', color: 'var(--muted)', marginTop: '4px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty states */}
      {!testimonials.length && (
        <p style={{ color: 'var(--muted)', fontSize: '14px', fontWeight: 300 }}>No recommendations yet.</p>
      )}
      {testimonials.length > 0 && !visible.length && (
        <p style={{ color: 'var(--muted)', fontSize: '14px', fontWeight: 300 }}>No recommendations in this period.</p>
      )}

      {/* Premium upsell */}
      {!isPremium && testimonials.length > 0 && (
        <div style={{ fontSize: '12px', color: 'var(--muted)', background: 'var(--cream)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', marginBottom: '4px' }}>
          Upgrade to Premium to pin a recommendation to the top of your public profile.
        </div>
      )}

      {/* Recommendation cards */}
      {visible.length > 0 && (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {visible.map(t => (
          <div key={t.id} style={{ background: 'var(--cream)', borderRadius: 'var(--radius-md)', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--navy)' }}>{t.author_name}</span>
                <span style={{ fontSize: '10px', padding: '2px 7px', borderRadius: '99px', background: t.attributed_side === 'Carrier' ? '#dbeafe' : '#dcfce7', color: t.attributed_side === 'Carrier' ? '#1d4ed8' : '#15803d', fontWeight: 500 }}>
                  {t.attributed_side}
                </span>
                <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
                  {new Date(t.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </span>
              </div>
              <button
                onClick={() => togglePin(t)}
                disabled={!isPremium || pinning === t.id}
                title={!isPremium ? 'Pinning recommendations is a Premium feature' : t.is_pinned ? 'Unpin from top' : 'Pin to top of profile'}
                style={{ background: 'none', border: 'none', fontSize: '16px', lineHeight: 1, padding: '2px 4px', cursor: isPremium && !pinning ? 'pointer' : 'not-allowed', opacity: !isPremium ? 0.25 : pinning === t.id ? 0.5 : 1, transition: 'opacity 0.2s' }}
              >
                {t.is_pinned ? '📌' : '📍'}
              </button>
            </div>

            {/* Recommendation text */}
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--navy)', lineHeight: 1.6, fontFamily: 'var(--serif)', fontStyle: 'italic' }}>
              "{t.content}"
            </p>

            {/* Existing reply (read-only view) */}
            {t.owner_reply && replyingTo !== t.id && (
              <div style={{ borderLeft: '3px solid var(--gold)', paddingLeft: '10px', marginTop: '2px' }}>
                <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Your reply</div>
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--navy)', lineHeight: 1.6 }}>{t.owner_reply}</p>
              </div>
            )}

            {/* Reply editor */}
            {replyingTo === t.id && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '2px' }}>
                <textarea
                  autoFocus
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  placeholder="Write your reply…"
                  maxLength={500}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '10px', fontSize: '13px', fontFamily: 'var(--sans)', color: 'var(--navy)', background: 'white', border: '1.5px solid var(--gold)', borderRadius: 'var(--radius-sm)', resize: 'vertical', minHeight: '80px', lineHeight: 1.6 }}
                />
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button
                    onClick={() => saveReply(t)}
                    disabled={savingReply}
                    style={{ padding: '6px 14px', background: 'var(--navy)', color: 'white', border: 'none', borderRadius: 'var(--radius-sm)', fontFamily: 'var(--sans)', fontSize: '12px', fontWeight: 500, cursor: savingReply ? 'not-allowed' : 'pointer', opacity: savingReply ? 0.6 : 1 }}
                  >
                    {savingReply ? 'Saving…' : 'Save reply'}
                  </button>
                  {replyText.trim() && t.owner_reply && (
                    <button
                      onClick={async () => { setReplyText(''); await saveReply({ ...t, owner_reply: null }); }}
                      disabled={savingReply}
                      style={{ padding: '6px 14px', background: 'transparent', color: '#dc2626', border: '1px solid #dc2626', borderRadius: 'var(--radius-sm)', fontFamily: 'var(--sans)', fontSize: '12px', cursor: 'pointer' }}
                    >
                      Remove reply
                    </button>
                  )}
                  <button
                    onClick={() => { setReplyingTo(null); setReplyText(''); }}
                    style={{ padding: '6px 14px', background: 'transparent', color: 'var(--muted)', border: 'none', fontFamily: 'var(--sans)', fontSize: '12px', cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <span style={{ fontSize: '11px', color: 'var(--muted)', marginLeft: 'auto' }}>{replyText.length}/500</span>
                </div>
              </div>
            )}

            {/* Reply button — premium only, not shown while editing */}
            {isPremium && replyingTo !== t.id && (
              <div>
                <button
                  onClick={() => { setReplyingTo(t.id); setReplyText(t.owner_reply || ''); }}
                  style={{ background: 'none', border: 'none', fontFamily: 'var(--sans)', fontSize: '12px', color: 'var(--muted)', cursor: 'pointer', padding: '0', textDecoration: 'underline' }}
                >
                  {t.owner_reply ? 'Edit reply' : 'Reply'}
                </button>
              </div>
            )}

          </div>
        ))}
      </div>
      )}
    </div>
  );
};

// ─── BILLING TAB ─────────────────────────────────────────────────────────────
const BillingTab = ({ userId, profile }) => {
  const [subscription, setSubscription] = useState(null);
  const [history,      setHistory]      = useState([]);
  const [loading,      setLoading]      = useState(true);

  useEffect(() => {
    if (!userId) return;
    const load = async () => {
      const [subResult, histResult] = await Promise.all([
        supabase
          .from('subscriptions')
          .select('tier, status, current_period_end, cancel_at_period_end')
          .eq('user_id', userId)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from('payment_history')
          .select('stripe_invoice_id, amount_cents, currency, status, period_start, period_end, paid_at')
          .eq('user_id', userId)
          .order('period_end', { ascending: false }),
      ]);
      if (subResult.error)  console.error('BillingTab subscription fetch error:', subResult.error.message);
      if (histResult.error) console.error('BillingTab payment_history fetch error:', histResult.error.message);
      setSubscription(subResult.data ?? null);
      setHistory(histResult.data || []);
      setLoading(false);
    };
    load();
  }, [userId]);

  const tier      = profile?.profile_tier;
  const tierLabel = tier === 'peer_reviewed' ? 'Peer Reviewed' : tier === 'premium' ? 'Premium' : 'Free';

  const statusBadge = (() => {
    if (subscription?.status === 'active')   return { label: 'Active',   color: '#16a34a', bg: '#dcfce7' };
    if (subscription?.status === 'unpaid')   return { label: 'Unpaid',   color: '#dc2626', bg: '#fee2e2' };
    if (subscription?.status === 'canceled') return { label: 'Canceled', color: 'var(--muted)', bg: 'var(--cream)' };
    return { label: 'Free', color: 'var(--muted)', bg: 'var(--cream)' };
  })();

  const formatCents = (cents, currency = 'usd') =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format((cents ?? 0) / 100);

  const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  const fmtPeriod = (start, end) =>
    (start && end) ? `${fmtDate(start)} – ${fmtDate(end)}` : '—';

  if (loading) return (
    <div className="dashboard-section-body" style={{ color: 'var(--muted)', fontSize: '14px' }}>Loading…</div>
  );

  return (
    <div className="dashboard-section-body">

      {/* ── Status card ── */}
      <div style={{ background: 'var(--cream)', borderRadius: 'var(--radius-md)', padding: '20px', marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <div>
            <div style={{ fontSize: '11px', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Current plan</div>
            <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--navy)', fontFamily: 'var(--serif)' }}>{tierLabel}</div>
          </div>
          <span style={{ fontSize: '12px', fontWeight: 600, padding: '4px 12px', borderRadius: '99px', color: statusBadge.color, background: statusBadge.bg }}>
            {statusBadge.label}
          </span>
        </div>

        {subscription?.status === 'active' && subscription?.current_period_end && (
          <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
            Renews {fmtDate(subscription.current_period_end)}
            {subscription.cancel_at_period_end && (
              <span style={{ marginLeft: '8px', color: '#dc2626' }}> · Cancels at period end</span>
            )}
          </div>
        )}

        {subscription?.status === 'unpaid' && (
          <div style={{ fontSize: '13px', color: '#dc2626' }}>
            Payment failed. Check your email for a message from Stripe with a link to update your card. Once updated, your payment will be retried automatically. Need help? Email us at <a href="mailto:support@umpire-iq.com" style={{ color: '#dc2626' }}>support@umpire-iq.com</a>.
          </div>
        )}
      </div>

      {/* ── Payment history ── */}
      <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px' }}>
        Payment History
      </div>

      {history.length === 0 ? (
        <p style={{ color: 'var(--muted)', fontSize: '14px', fontWeight: 300 }}>No payment history yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr 90px 70px', gap: '8px', fontSize: '11px', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '0 4px 8px', borderBottom: '1px solid var(--border)' }}>
            <span>Date</span><span>Period</span><span>Amount</span><span>Status</span>
          </div>
          {history.map((row, i) => {
            const isPaid = row.status === 'paid';
            return (
              <div key={row.stripe_invoice_id || i} style={{ display: 'grid', gridTemplateColumns: '110px 1fr 90px 70px', gap: '8px', fontSize: '13px', color: 'var(--navy)', padding: '10px 4px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
                <span style={{ fontSize: '12px' }}>{fmtDate(row.paid_at || row.period_end)}</span>
                <span style={{ color: 'var(--muted)', fontSize: '12px' }}>{fmtPeriod(row.period_start, row.period_end)}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: '13px' }}>{formatCents(row.amount_cents, row.currency)}</span>
                <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '99px', color: isPaid ? '#16a34a' : '#dc2626', background: isPaid ? '#dcfce7' : '#fee2e2', textAlign: 'center', whiteSpace: 'nowrap' }}>
                  {isPaid ? 'Paid' : 'Failed'}
                </span>
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
};

// ─── SETTINGS TAB ────────────────────────────────────────────────────────────
const SettingsTab = ({ profile, isPremium, isStaff, pii, authEmail, onSave, onSavePii }) => {
  const [settings, setSettings] = useState({
    is_visible:    profile?.is_visible    ?? true,
    is_searchable: profile?.is_searchable ?? false,
    show_contact:  profile?.show_contact  ?? false,
    show_email:    profile?.show_email    ?? true,
    show_phone:    profile?.show_phone    ?? true,
  });
  const [contact, setContact] = useState({
    contact_phone: pii?.contact_phone || '',
    contact_email: pii?.contact_email || '',
    address:       pii?.address       || '',
    date_of_birth: pii?.date_of_birth || '',
  });
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);

  useEffect(() => {
    setContact({
      contact_phone: pii?.contact_phone || '',
      contact_email: pii?.contact_email || '',
      address:       pii?.address       || '',
      date_of_birth: pii?.date_of_birth || '',
    });
  }, [pii]);

  const update        = (key, val) => setSettings(s => ({ ...s, [key]: val }));
  const updateContact = (key, val) => setContact(c => ({ ...c, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    await onSave(settings);
    await onSavePii({
      contact_phone: contact.contact_phone,
      contact_email: authEmail || null,
      address:       contact.address,
      date_of_birth: contact.date_of_birth || null,
    });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="dashboard-section-body">

      <div className="settings-group">
        {isStaff && (
          <div className="settings-row">
            <div className="settings-row-left">
              <div className="settings-row-label">
                Profile visible
                <span style={{ color: 'var(--gold)', fontSize: '11px', fontWeight: 600, marginLeft: '8px' }}>Staff only</span>
              </div>
              <div className="settings-row-desc">Show your profile to signed-in users on the browse screen</div>
            </div>
            <Toggle checked={settings.is_visible} onChange={v => update('is_visible', v)} />
          </div>
        )}

        <div className="settings-row">
          <div className="settings-row-left">
            <div className="settings-row-label">
              Appear in filtered searches
              {!isPremium && <span style={{ color: 'var(--gold)', fontSize: '11px', marginLeft: '8px' }}>Premium</span>}
            </div>
            <div className="settings-row-desc">Show up in certification and county-level searches</div>
          </div>
          <Toggle
            checked={isPremium ? settings.is_searchable : false}
            onChange={v => isPremium && update('is_searchable', v)}
          />
        </div>

        <div className="settings-row">
          <div className="settings-row-left">
            <div className="settings-row-label">Show contact button</div>
            <div className="settings-row-desc">Allow others to contact you from your profile</div>
          </div>
          <Toggle checked={settings.show_contact} onChange={v => update('show_contact', v)} />
        </div>

        {settings.show_contact && (
          <div style={{ paddingLeft: '16px', borderLeft: '2px solid var(--border)', marginLeft: '8px' }}>

            <div className="edit-field">
              <label className="edit-label">Contact email</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  className="edit-input"
                  type="email"
                  value={authEmail}
                  readOnly
                  style={{ background: 'var(--cream)', color: 'var(--muted)', cursor: 'default' }}
                />
                <span style={{ fontSize: '11px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>Login email</span>
              </div>
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
                Your login email is shared as your contact. Manage it in account settings.
              </div>
            </div>

            <div className="settings-row">
              <div className="settings-row-left">
                <div className="settings-row-label">Show email to others ✉️</div>
                <div className="settings-row-desc">Display email contact button on your profile</div>
              </div>
              <Toggle checked={settings.show_email} onChange={v => update('show_email', v)} />
            </div>

            <div className="edit-field">
              <label className="edit-label">Contact phone</label>
              <input
                className="edit-input"
                type="tel"
                placeholder="(555) 555-5555"
                value={contact.contact_phone}
                onChange={e => updateContact('contact_phone', e.target.value)}
              />
            </div>

            <div className="settings-row">
              <div className="settings-row-left">
                <div className="settings-row-label">Show phone to others 📞</div>
                <div className="settings-row-desc">Display phone contact button on your profile</div>
              </div>
              <Toggle checked={settings.show_phone} onChange={v => update('show_phone', v)} />
            </div>

          </div>
        )}
      </div>

      <div className="settings-group">
        <div className="settings-row">
          <div className="settings-row-left">
            <div className="settings-row-label">Personal information</div>
            <div className="settings-row-desc">Stored encrypted — never shown to other users</div>
          </div>
        </div>

        <div className="edit-field">
          <label className="edit-label">Mailing address</label>
          <input
            className="edit-input"
            type="text"
            placeholder="Street, City, State, ZIP"
            value={contact.address}
            onChange={e => updateContact('address', e.target.value)}
          />
        </div>

      </div>

      <div className="edit-save-row">
        <button className="edit-save-btn" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save Settings'}
        </button>
      </div>

    </div>
  );
};

// ─── TESTIMONIALS TAB ──────────────────────────────────────────────────────────

// ─── VERIFICATION MODAL ──────────────────────────────────────────────────────
const NPN_LICENSE_TYPES = ['PA', 'IA'];

const VerificationModal = ({ licenseType, homeState, onClose }) => {
  const [licenseNumber, setLicenseNumber] = useState('');
  const [state,         setState]         = useState(homeState !== '—' ? homeState : '');
  const [npn,           setNpn]           = useState('');
  const [submitting,    setSubmitting]    = useState(false);
  const [error,         setError]         = useState('');
  const [authority,     setAuthority]     = useState(null);

  const npnRequired = NPN_LICENSE_TYPES.includes(licenseType);

  // Fetch verification authority whenever state changes to detect manual-only combos
  useEffect(() => {
    if (!state) { setAuthority(null); return; }
    supabase
      .from('verification_authority')
      .select('state, license_type, authority_name, search_url')
      .then(({ data }) => {
        const rows = data || [];
        setAuthority(
          rows.find(a => a.state === state && a.license_type === licenseType) ||
          rows.find(a => a.state === state && !a.license_type) ||
          rows.find(a => !a.state && a.license_type === licenseType) ||
          rows.find(a => !a.state && !a.license_type) ||
          null
        );
      });
  }, [state, licenseType]);

  const US_STATES = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
    'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
    'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
    'VA','WA','WV','WI','WY','DC',
  ];

  const handleSubmit = async () => {
    if (!licenseNumber.trim()) { setError('License number is required.'); return; }
    if (!state)                { setError('Home state is required.'); return; }
    if (npnRequired && !npn.trim()) { setError('NPN is required for your license type.'); return; }

    setSubmitting(true);
    setError('');

    const { data, error: fnErr } = await supabase.functions.invoke('create-verification-checkout', {
      body: { license_number: licenseNumber.trim(), home_state: state, npn: npn.trim() || null },
    });

    if (fnErr || !data?.url) {
      console.error('VerificationModal: checkout creation error:', fnErr?.message || 'no URL returned');
      setError(data?.error || fnErr?.message || 'Failed to start checkout. Please try again.');
      setSubmitting(false);
      return;
    }

    window.location.href = data.url;
  };

  const handleOverlay = (e) => { if (e.target === e.currentTarget) onClose(); };

  return (
    <div className="modal-overlay" onClick={handleOverlay} style={{ zIndex: 1100 }}>
      <div className="modal" style={{ maxWidth: '480px' }}>
        <div className="modal-header" style={{ borderBottom: '1px solid var(--border)', paddingBottom: '16px' }}>
          <div className="modal-identity">
            <div className="modal-name" style={{ fontSize: '16px' }}>Get Verified</div>
            <div style={{ fontSize: '13px', color: 'var(--muted)', marginTop: '2px' }}>$5.00</div>
          </div>
          <button className="modal-close" onClick={onClose} disabled={submitting}>×</button>
        </div>

        <div className="modal-body" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {error && (
            <div style={{ fontSize: '13px', color: '#dc2626', background: '#fee2e2', borderRadius: 'var(--radius-sm)', padding: '10px 12px' }}>
              {error}
            </div>
          )}

          <div className="edit-field">
            <label className="edit-label">Home license number <span style={{ color: 'red' }}>*</span></label>
            <input
              className="edit-input"
              type="text"
              placeholder="e.g. A123456"
              value={licenseNumber}
              onChange={e => setLicenseNumber(e.target.value)}
              disabled={submitting}
            />
          </div>

          <div className="edit-field">
            <label className="edit-label">Home state <span style={{ color: 'red' }}>*</span></label>
            <select
              className="edit-input"
              value={state}
              onChange={e => setState(e.target.value)}
              disabled={submitting}
            >
              <option value="">Select state…</option>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div className="edit-field">
            <label className="edit-label">
              NPN {npnRequired
                ? <span style={{ color: 'red' }}>*</span>
                : <span style={{ fontSize: '11px', color: 'var(--muted)', fontWeight: 300 }}>(optional)</span>
              }
            </label>
            <input
              className="edit-input"
              type="text"
              placeholder="National Producer Number"
              value={npn}
              onChange={e => setNpn(e.target.value.replace(/\D/g, ''))}
              maxLength={10}
              disabled={submitting}
            />

          </div>

          {authority?.search_url === 'manual' ? (
            <div style={{ fontSize: '13px', background: '#fef3c7', border: '1px solid #d97706', borderRadius: 'var(--radius-sm)', padding: '12px', lineHeight: 1.6, color: '#92400e' }}>
              <strong>Email required:</strong> Your license type in {state} is verified on a county-by-county basis and cannot be looked up centrally. After completing payment, email a copy of your license to <strong>admin@umpire-iq.com</strong> to complete your request.
            </div>
          ) : (
            <div style={{ fontSize: '12px', color: 'var(--muted)', background: 'var(--cream)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', lineHeight: 1.6 }}>
              After payment, your request will be verified either automatically with NIPR, or manually — typically within 1–2 business days. You'll keep full access to the platform while you wait. The fee helps us offset NIPR's api fee and or costs of manual license verifications.
            </div>
          )}

          <div className="edit-save-row" style={{ marginTop: '0' }}>
            <button
              className="edit-save-btn"
              onClick={handleSubmit}
              disabled={submitting || !licenseNumber.trim() || !state || (npnRequired && !npn.trim())}
            >
              {submitting ? 'Opening checkout…' : 'Continue to Payment →'}
            </button>
            <button
              className="edit-save-btn"
              style={{ marginLeft: '8px', background: 'transparent', color: 'var(--muted)' }}
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── SURVEY PROPOSAL FORM ────────────────────────────────────────────────────
const SurveyProposalForm = ({ userId, isPremium }) => {
  const [text,       setText]       = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(false);

  const handleSubmit = async () => {
    if (!text.trim() || submitting) return;
    setSubmitting(true);
    const { error } = await supabase
      .from('survey_question_proposals')
      .insert({ user_id: userId, question_text: text.trim() });
    setSubmitting(false);
    if (!error) { setText(''); setSubmitted(true); }
  };

  return (
    <div className="survey-proposal-section">
      <div className="survey-archive-date" style={{ marginBottom: '8px' }}>
        Propose a Question
        {!isPremium && (
          <span style={{ color: 'var(--gold)', fontSize: '11px', fontWeight: 600, marginLeft: '8px' }}>
            Premium
          </span>
        )}
      </div>
      <p className="survey-proposal-desc">
        Want to tell the carriers something? Want to know what the industry thinks? 
        Got a question that's been bugging you?
        <br />Submit a question for consideration, we will review and may feature it.
      </p>
      {submitted ? (
        <div className="survey-proposal-thanks">
          ✓ Submitted — we'll review it for an upcoming poll. Thank you.
        </div>
      ) : (
        <>
          <textarea
            className="survey-proposal-input"
            placeholder="What would you like to ask the industry?"
            value={isPremium ? text : ''}
            onChange={e => isPremium && setText(e.target.value)}
            disabled={!isPremium}
            rows={3}
          />
          <button
            className="survey-archive-submit"
            onClick={handleSubmit}
            disabled={!isPremium || !text.trim() || submitting}
            style={{ marginTop: '12px' }}
          >
            {submitting ? 'Submitting…' : 'Submit for Consideration'}
          </button>
          {!isPremium && (
            <p style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '10px' }}>
              Upgrade to Premium to submit proposed questions.
            </p>
          )}
        </>
      )}
    </div>
  );
};

// ─── SURVEYS TAB ─────────────────────────────────────────────────────────────
const SurveysTab = ({ userId, isPremium }) => {
  const [questions,       setQuestions]       = useState([]);
  const [responses,       setResponses]       = useState({});
  const [totals,          setTotals]          = useState({});
  const [selected,        setSelected]        = useState({});
  const [voting,          setVoting]          = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [availableMonths, setAvailableMonths] = useState([]); // [{year, month, label}]
  const [browseYear,      setBrowseYear]      = useState('');
  const [browseMonth,     setBrowseMonth]     = useState('');
  const [browsing,        setBrowsing]        = useState(false); // false = "last 4" view, true = filtered view

  // Build the list of months that actually have archived surveys, once
  useEffect(() => {
    if (!isPremium) return;
    supabase
      .from('survey_questions')
      .select('opens_at')
      .eq('status', 'archived')
      .order('opens_at', { ascending: false })
      .then(({ data }) => {
        const seen = new Set();
        const months = [];
        (data || []).forEach(row => {
          const d = new Date(row.opens_at);
          const key = `${d.getFullYear()}-${d.getMonth()}`;
          if (!seen.has(key)) {
            seen.add(key);
            months.push({
              year: d.getFullYear(),
              month: d.getMonth(),
              label: d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
            });
          }
        });
        setAvailableMonths(months);
      });
  }, [isPremium]);

  const loadQuestions = useCallback(async (filterYear, filterMonth) => {
    setLoading(true);

    let query = supabase
      .from('survey_questions')
      .select('*')
      .eq('status', 'archived')
      .order('opens_at', { ascending: false });

    if (filterYear != null && filterMonth != null) {
      const start = new Date(Date.UTC(filterYear, filterMonth, 1));
      const end   = new Date(Date.UTC(filterYear, filterMonth + 1, 1));
      query = query.gte('opens_at', start.toISOString()).lt('opens_at', end.toISOString());
    } else {
      query = query.limit(4);
    }

    const { data: qs } = await query;

    if (!qs?.length) { setQuestions([]); setLoading(false); return; }

    const { data: allOptions } = await supabase
      .from('survey_answer_options')
      .select('id, question_id, label, sort_order')
      .in('question_id', qs.map(q => q.id))
      .order('sort_order', { ascending: true });

    const optionsByQuestion = {};
    (allOptions || []).forEach(opt => {
      if (!optionsByQuestion[opt.question_id]) optionsByQuestion[opt.question_id] = [];
      optionsByQuestion[opt.question_id].push({ id: opt.id, label: opt.label });
    });
    qs.forEach(q => { q.options = optionsByQuestion[q.id] || []; });

    setQuestions(qs);

    const { data: resps } = await supabase
      .from('survey_responses')
      .select('question_id, answer_option_id')
      .eq('user_id', userId)
      .in('question_id', qs.map(q => q.id));

    const respMap = {};
    resps?.forEach(r => { respMap[r.question_id] = r.answer_option_id; });
    setResponses(respMap);

    const totalsMap = {};
    await Promise.all(qs.map(async q => {
      const { data: t } = await supabase.rpc('get_survey_totals', { p_question_id: q.id });
      const m = {};
      t?.forEach(row => { m[row.answer_option_id] = Number(row.response_count); });
      totalsMap[q.id] = m;
    }));
    setTotals(totalsMap);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    if (!isPremium || !userId) { setLoading(false); return; }
    loadQuestions(null, null);
  }, [isPremium, userId, loadQuestions]);

  const applyMonthFilter = () => {
    if (browseYear === '' || browseMonth === '') return;
    setBrowsing(true);
    loadQuestions(Number(browseYear), Number(browseMonth));
  };

  const backToRecent = () => {
    setBrowsing(false);
    setBrowseYear('');
    setBrowseMonth('');
    loadQuestions(null, null);
  };

  const years = [...new Set(availableMonths.map(m => m.year))].sort((a, b) => b - a);
  const monthsForYear = availableMonths.filter(m => m.year === Number(browseYear));

  const handleVote = async (questionId) => {
    const optionId = selected[questionId];
    if (!optionId || voting) return;
    setVoting(questionId);
    const { error } = await supabase
      .from('survey_responses')
      .upsert(
        { question_id: questionId, user_id: userId, answer_option_id: optionId },
        { onConflict: 'question_id,user_id' }
      );
    if (!error) {
      setResponses(prev => ({ ...prev, [questionId]: optionId }));
      const { data: t } = await supabase.rpc('get_survey_totals', { p_question_id: questionId });
      const m = {};
      t?.forEach(row => { m[row.answer_option_id] = Number(row.response_count); });
      setTotals(prev => ({ ...prev, [questionId]: m }));
    }
    setVoting(null);
  };

  const browseControls = () => (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px', padding: '12px', background: 'var(--cream)', borderRadius: 'var(--radius-sm)' }}>
      <span style={{ fontSize: '12px', color: 'var(--muted)', fontWeight: 500 }}>Browse older surveys:</span>
      <select
        className="edit-input"
        style={{ width: 'auto', padding: '6px 10px', fontSize: '13px' }}
        value={browseYear}
        onChange={e => { setBrowseYear(e.target.value); setBrowseMonth(''); }}
      >
        <option value="">Year…</option>
        {years.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
      <select
        className="edit-input"
        style={{ width: 'auto', padding: '6px 10px', fontSize: '13px' }}
        value={browseMonth}
        onChange={e => setBrowseMonth(e.target.value)}
        disabled={browseYear === ''}
      >
        <option value="">Month…</option>
        {monthsForYear.map(m => (
          <option key={m.month} value={m.month}>
            {new Date(2000, m.month, 1).toLocaleDateString('en-US', { month: 'long' })}
          </option>
        ))}
      </select>
      <button
        className="edit-save-btn"
        style={{ fontSize: '12px', padding: '6px 14px' }}
        onClick={applyMonthFilter}
        disabled={browseYear === '' || browseMonth === ''}
      >
        View
      </button>
      {browsing && (
        <button
          onClick={backToRecent}
          style={{ background: 'none', border: 'none', color: 'var(--gold)', fontSize: '12px', cursor: 'pointer', fontFamily: 'var(--sans)', textDecoration: 'underline' }}
        >
          ← Back to recent
        </button>
      )}
    </div>
  );

  const archiveSection = () => {
    if (!isPremium) {
      return (
        <div className="surveys-locked">
          <div className="surveys-locked-icon">🔒</div>
          <div className="surveys-locked-title">Survey Archive</div>
          <p className="surveys-locked-body">
            Premium members get full access to the survey archive — browse every past question and contribute your answer even after Tuesday closes.
          </p>
          <button className="tier-upgrade-btn">Upgrade to Premium</button>
        </div>
      );
    }

    return (
      <div>
        {availableMonths.length > 0 && browseControls()}

        {loading ? (
          <div className="surveys-loading">Loading survey archive…</div>
        ) : !questions.length ? (
          <div className="surveys-empty">
            {browsing
              ? 'No archived surveys for that month.'
              : 'No archived surveys yet. The first question closes Tuesday at midnight ET.'}
          </div>
        ) : (
          <div className="surveys-list">
            {questions.map(q => {
              const answered = responses[q.id];
              const qTotals  = totals[q.id] || {};
              const total    = Object.values(qTotals).reduce((a, b) => a + b, 0);
              return (
                <div key={q.id} className="survey-archive-item">
                  <div className="survey-archive-date">
                    Week of {new Date(q.opens_at).toLocaleDateString('en-US', {
                      month: 'long', day: 'numeric', year: 'numeric',
                    })}
                  </div>
                  <div className="survey-archive-question">{q.question_text}</div>
                  {!answered ? (
                    <>
                      <div className="survey-archive-options">
                        {q.options.map(opt => (
                          <div
                            key={opt.id}
                            className={`survey-modal-option ${selected[q.id] === opt.id ? 'selected' : ''}`}
                            onClick={() => setSelected(prev => ({ ...prev, [q.id]: opt.id }))}
                          >
                            <div className="survey-modal-radio">
                              {selected[q.id] === opt.id && <div className="survey-modal-radio-dot" />}
                            </div>
                            <span className="survey-modal-option-label">{opt.label}</span>
                          </div>
                        ))}
                      </div>
                      <button
                        className="survey-archive-submit"
                        onClick={() => handleVote(q.id)}
                        disabled={!selected[q.id] || voting === q.id}
                      >
                        {voting === q.id ? 'Submitting…' : 'Submit'}
                      </button>
                    </>
                  ) : (
                    <div className="survey-archive-results">
                      {q.options.map(opt => {
                        const count      = qTotals[opt.id] || 0;
                        const pct        = total > 0 ? Math.round((count / total) * 100) : 0;
                        const isMyAnswer = answered === opt.id;
                        return (
                          <div key={opt.id} className={`survey-result-row ${isMyAnswer ? 'my-answer' : ''}`}>
                            <div className="survey-result-label">
                              {isMyAnswer && <span className="survey-result-you">✓ </span>}
                              {opt.label}
                            </div>
                            <div className="survey-result-bar-wrap">
                              <div className="survey-result-bar" style={{ width: `${pct}%` }} />
                            </div>
                            <div className="survey-result-pct">
                              {pct}% <span className="survey-result-count">({count})</span>
                            </div>
                          </div>
                        );
                      })}
                      <div className="survey-result-total">
                        {total} total {total === 1 ? 'response' : 'responses'}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {archiveSection()}
      <SurveyProposalForm userId={userId} isPremium={isPremium} />
    </div>
  );
};

// ─── MAIN DASHBOARD ──────────────────────────────────────────────────────────
const ManageTab = ({ onNavigate }) => (
  <div className="dashboard-section-body">
    <p style={{ color: 'var(--muted)', fontSize: '14px', marginBottom: '24px', fontWeight: 300, lineHeight: 1.7 }}>
      Access the UmpireIQ back office to manage users, review verification requests, moderate content, and administer the platform.
    </p>
    <button 
      className="edit-save-btn"
      onClick={() => onNavigate('backoffice')}
    >
      Go to Back Office →
    </button>
  </div>
);

const Dashboard = ({ onNavigate, verificationResult }) => {
  const [activeTab,            setActiveTab]            = useState('profile');
  const [showWelcome,          setShowWelcome]          = useState(true);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [profile,              setProfile]              = useState(null);
  const [licenses,             setLicenses]             = useState([]);
  const [user,                 setUser]                 = useState(null);
  const [pii,                  setPii]                  = useState(null);
  const [authEmail,            setAuthEmail]            = useState('');
  const [loading,              setLoading]              = useState(true);

    // Fetch user + profile + licenses
    const fetchData = useCallback(async () => {
    setLoading(true);
    
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) { onNavigate('login'); return; }
    setAuthEmail(authUser.email || '');

    const { data: userData } = await supabase
      .from('users')
      .select('*')
      .eq('auth_id', authUser.id)
      .single();
    
    const { data: profileData } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', userData?.id)
      .single();
    
    const { data: licenseData } = await supabase
      .from('licenses')
      .select('*')
      .eq('user_id', userData?.id)
      .order('license_type', { ascending: true });

    setUser(userData);
    // Check if account disabled (takedown)
    if (userData?.is_active === false) {
      await supabase.auth.signOut();
      onNavigate('home');
      return;
    }

    // Heartbeat for "active now" tracking
    if (profileData?.id) {
      supabase.from('profiles').update({ last_active_at: new Date().toISOString() }).eq('id', profileData.id);
    }
    setProfile(profileData);
    setLicenses(licenseData || []);

    // Decrypted PII (contact info, address, DOB, NPN) for editing
    const { data: piiData, error: piiError } = await supabase.rpc('get_my_pii').single();
    if (!piiError) setPii(piiData);

    setLoading(false);
  }, [onNavigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    onNavigate('home');
  };

  const saveProfile = async (updates) => {
    if (!profile?.id) return;
    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', profile.id);
    if (error) console.error('Profile save error:', error.message);
    else setProfile(prev => ({ ...prev, ...updates }));
  };

  // Contact phone/email, address, and date of birth are stored encrypted —
  // routed through the update_my_pii RPC instead of a direct table update.
  const savePii = async (updates) => {
    const clearFields = Object.entries(updates)
      .filter(([, v]) => v === '' || v === null)
      .map(([k]) => k);

    const { error } = await supabase.rpc('update_my_pii', {
      p_contact_phone: updates.contact_phone || null,
      p_contact_email: updates.contact_email || null,
      p_address:       updates.address       || null,
      p_date_of_birth: updates.date_of_birth || null,
      p_clear_fields:  clearFields,
    });
    if (error) console.error('PII save error:', error.message);
    else setPii(prev => ({ ...prev, ...updates }));
  };

  const isPremium = profile?.profile_tier === 'premium' || profile?.profile_tier === 'peer_reviewed';

  const tabKeys = user?.is_staff
    ? ['profile', 'licenses', 'testimonials', 'surveys', 'billing', 'settings', 'manage']
    : ['profile', 'licenses', 'testimonials', 'surveys', 'billing', 'settings'];

  const scrollerRef = React.useRef(null);
  const sectionRefs = React.useRef({});
  const isProgrammaticScroll = React.useRef(false);

  const goToTab = (tab) => {
    setActiveTab(tab);
    const el = sectionRefs.current[tab];
    if (el) {
      isProgrammaticScroll.current = true;
      el.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
      setTimeout(() => { isProgrammaticScroll.current = false; }, 500);
    }
  };

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    let raf = null;
    const handleScroll = () => {
      if (isProgrammaticScroll.current) return;
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const idx = Math.round(scroller.scrollLeft / scroller.clientWidth);
        const tab = tabKeys[idx];
        if (tab) setActiveTab(prev => (prev === tab ? prev : tab));
      });
    };
    scroller.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      scroller.removeEventListener('scroll', handleScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [tabKeys]);

  const tabButtonRefs = React.useRef({});

  useEffect(() => {
    const btn = tabButtonRefs.current[activeTab];
    if (btn) {
      btn.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
    }
  }, [activeTab]);

  // Completion percentage
  const completionItems = [
  { done: !!profile?.display_name,           label: 'Add a display name',   pct: 15 },
  { done: !!profile?.bio,                     label: 'Write a bio',          pct: 15 },
  { done: licenses.length > 0,                label: 'Verify your license',  pct: 25 },
  { done: !!profile?.certifications?.length,  label: 'Add certifications',   pct: 10 },
  { done: !!profile?.loss_types?.length,      label: 'Select loss types you handle', pct: 10 },
  { done: !!profile?.estimating_software?.length, label: 'Select estimating software', pct: 10 },
  { done: isPremium,                          label: 'Upgrade to Premium',   pct: 15 },
];
  const completion = completionItems.reduce((sum, i) => sum + (i.done ? i.pct : 0), 0);
  const nextAction = completionItems.find(i => !i.done);

  // Display name fallback
  const displayName = profile?.display_name || 'Your Name';

  const initials = displayName
    .split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  // License type from home license
  const homeLicense   = licenses.find(l => l.is_home);
  const licenseType   = homeLicense?.license_type || '—';
  const homeState     = homeLicense?.state || '—';

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--cream)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: 'var(--muted)', fontSize: '14px' }}>
          <div className="signup-spinner" />
          Loading your profile...
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">

      {showVerificationModal && (
        <VerificationModal
          licenseType={licenseType}
          homeState={homeState}
          onClose={() => setShowVerificationModal(false)}
        />
      )}

      {/* ── NAV ── */}
      <nav className="dashboard-nav">
        <div className="dashboard-nav-logo" onClick={() => onNavigate('home')}>
          Umpire<span>IQ</span>
        </div>
        <div className="dashboard-nav-right">
          <span className="dashboard-nav-name">{displayName}</span>
          <NotificationBell userId={user?.id} />
          <button className="dashboard-nav-signout" onClick={handleSignOut}>Sign out</button>
        </div>
      </nav>

      <div className="dashboard-body">

        {/* ── WELCOME BANNER ── */}
        {showWelcome && (
          <div className="dashboard-welcome">
            <div className="dashboard-welcome-text">
              👋 Welcome to UmpireIQ — complete your profile.
            </div>
            <button className="dashboard-welcome-dismiss" onClick={() => setShowWelcome(false)}>×</button>
          </div>
        )}

        {/* ── LEFT SIDEBAR ── */}
        <div className="profile-sidebar">

          {/* Profile card */}
          <div className="profile-card-dashboard">

            {/* Headshot / avatar */}
            <div className="profile-headshot-wrap">
              {profile?.headshot_url
                ? <img
                    className="profile-headshot"
                    src={profile.headshot_url}
                    alt={displayName}
                    onError={e => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }}
                  />
                : null
              }
              <div
                className="profile-headshot-placeholder"
                style={{ display: profile?.headshot_url ? 'none' : 'flex' }}
              >
                {initials}
              </div>
              {!isPremium && (
                <div className="profile-headshot-lock" title="Premium feature">🔒</div>
              )}
            </div>

            {/* Badges */}
            <div className="profile-badges">
              {profile?.peer_reviewed && (
                <span className="profile-badge peer-reviewed">Peer Reviewed</span>
              )}
              <span className="profile-badge license-type">{licenseType}</span>
              <span className="profile-badge state">{homeState}</span>
            </div>

            <div className="profile-display-name">{displayName}</div>
            <div className="profile-role">
              {profile?.primary_role || '—'} · {profile?.side || '—'}
            </div>

            {/* Verification badge */}
            {profile?.verification_badge === 'verified' && (() => {
              const expiresAt = profile.verified_time
                ? new Date(new Date(profile.verified_time).setFullYear(new Date(profile.verified_time).getFullYear() + 1))
                : null;
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#16a34a', fontWeight: 500 }}>
                    ✅ License Verified
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                    {isPremium
                      ? 'No expiration'
                      : expiresAt
                        ? `Expires ${expiresAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                        : null}
                  </div>
                </div>
              );
            })()}
            {profile?.verification_badge === 'pending' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--gold)', fontWeight: 500 }}>
                ⏳ Under review — you'll hear back within 2–3 business days
              </div>
            )}
            {profile?.verification_badge === 'failed' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#dc2626', fontWeight: 500 }}>
                ❌ License Verification Failed
              </div>
            )}
            {profile?.verification_badge === 'expired' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#dc2626', fontWeight: 500 }}>
                🔴 Verification Expired — renew to restore your badge
              </div>
            )}

            {/* Get Verified button — show for unverified, failed, and expired */}
            {profile?.verification_badge !== 'verified' && profile?.verification_badge !== 'pending' && (
              <button
                className="get-verified-btn"
                onClick={() => setShowVerificationModal(true)}
                style={{
                  marginTop: '6px', padding: '7px 16px', fontSize: '13px', fontWeight: 500,
                  fontFamily: 'var(--sans)', cursor: 'pointer', borderRadius: 'var(--radius-sm)',
                  border: '1.5px solid var(--navy)', background: 'transparent', color: 'var(--navy)',
                  transition: 'all 0.2s',
                }}
              >
                Get Verified
              </button>
            )}

            {/* Verification result banner (from Stripe redirect) */}
            {verificationResult === 'submitted' && (
              <div style={{ marginTop: '8px', padding: '10px 14px', background: '#dcfce7', border: '1px solid #16a34a', borderRadius: 'var(--radius-sm)', fontSize: '13px', color: '#15803d', fontWeight: 500, lineHeight: 1.5 }}>
                ✅ Verification submitted — we'll review your request within 2–3 business days.
              </div>
            )}
            {verificationResult === 'cancelled' && (
              <div style={{ marginTop: '8px', padding: '10px 14px', background: '#fef3c7', border: '1px solid #d97706', borderRadius: 'var(--radius-sm)', fontSize: '13px', color: '#92400e', fontWeight: 500, lineHeight: 1.5 }}>
                Checkout cancelled — your payment was not charged. Click "Get Verified" to try again.
              </div>
            )}

            {/* Star ratings */}
            <div className="profile-ratings">
              <div className="profile-rating-row">
                <span className="profile-rating-label">Overall</span>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Stars rating={profile?.overall_rating_avg || 0} size={12} />
                  <span className="profile-rating-avg">
                    {profile?.overall_rating_avg ? profile.overall_rating_avg.toFixed(1) : '—'}
                  </span>
                  <span className="profile-rating-count">({profile?.rating_count || 0})</span>
                </div>
              </div>
              <div className="profile-rating-row">
                <span className="profile-rating-label">IA</span>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Stars rating={profile?.ia_rating_avg || 0} size={12} />
                  <span className="profile-rating-avg">
                    {profile?.ia_rating_avg ? profile.ia_rating_avg.toFixed(1) : '—'}
                  </span>
                </div>
              </div>
              <div className="profile-rating-row">
                <span className="profile-rating-label">PA</span>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Stars rating={profile?.pa_rating_avg || 0} size={12} />
                  <span className="profile-rating-avg">
                    {profile?.pa_rating_avg ? profile.pa_rating_avg.toFixed(1) : '—'}
                  </span>
                </div>
              </div>
            </div>

            {/* Completion */}
            <div className="profile-completion">
              <div className="profile-completion-header">
                <span className="profile-completion-label">Profile complete</span>
                <span className="profile-completion-pct">{completion}%</span>
              </div>
              <div className="profile-completion-track">
                <div className="profile-completion-fill" style={{ width: `${completion}%` }} />
              </div>
              {nextAction && (
                <div className="profile-completion-next">
                  → {nextAction.label}
                </div>
              )}
            </div>

            <button
              onClick={() => onNavigate('search')}
              style={{
                width: '100%',
                marginTop: '12px',
                padding: '10px',
                background: 'var(--gold)',
                border: '1.5px solid var(--gold)',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--sans)',
                fontSize: '13px',
                fontWeight: 500,
                color: 'var(--white)',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.target.style.opacity = '0.85'; }}
              onMouseLeave={e => { e.target.style.opacity = '1'; }}
            >
              🔍 Find &amp; rate umpires
            </button>



          </div>

          {/* Tier status */}
          <div className="tier-status-card">
            <div className="tier-status-label">Current plan</div>
            <div className="tier-status-name">
              {profile?.profile_tier === 'peer_reviewed' ? 'Peer Reviewed'
               : profile?.profile_tier === 'premium'     ? 'Premium'
               : 'Free'}
            </div>
            {!isPremium && (
              <button 
                className="tier-upgrade-btn"
                onClick={() => window.open('https://buy.stripe.com/6oU00k2Lr2Jy8uI28i6wE00', '_blank')}
              >
                Upgrade to Premium — $25/mo
              </button>
            )}
            {isPremium && profile?.profile_tier !== 'peer_reviewed' && (
              <div title="Coming September 26, 2026" style={{ cursor: 'not-allowed' }}>
                <button
                  className="tier-upgrade-btn"
                  disabled
                  style={{ opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' }}
                >
                  Apply for Peer Review — Coming September 26, 2026
                </button>
              </div>
            )}
          </div>

        </div>

        {/* ── MAIN CONTENT ── */}
        <div className="dashboard-main">

          {/* Tabs — desktop only */}
          <div className="dashboard-tabs">
            {tabKeys.map(tab => (
              <button
                key={tab}
                ref={el => (tabButtonRefs.current[tab] = el)}
                className={`dashboard-tab ${activeTab === tab ? 'active' : ''}`}
                onClick={() => goToTab(tab)}
              >
                {tab === 'testimonials' ? 'Recommendations' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          <div className="dashboard-sections-scroll" ref={scrollerRef}>

          {/* Profile section */}
          <div className={`dashboard-section ${activeTab !== 'profile' ? 'hidden' : ''}`} data-tab="profile" ref={el => (sectionRefs.current.profile = el)}>
            <div className="dashboard-section-header">
              <div className="dashboard-section-title">Profile Information</div>
            </div>
            <ProfileTab
              profile={profile}
              licenses={licenses}
              isPremium={isPremium}
              onSave={saveProfile}
              userId={user?.id}
            />
          </div>

          {/* Licenses section */}
          <div className={`dashboard-section ${activeTab !== 'licenses' ? 'hidden' : ''}`} data-tab="licenses" ref={el => (sectionRefs.current.licenses = el)}>
            <div className="dashboard-section-header">
              <div className="dashboard-section-title">Licenses & Credentials</div>
            </div>
            <LicensesTab 
              licenses={licenses}
              userId={user?.id}
              onRefresh={fetchData} />
          </div>

          {/* Testimonials section */}
          <div className={`dashboard-section ${activeTab !== 'testimonials' ? 'hidden' : ''}`} data-tab="testimonials" ref={el => (sectionRefs.current.testimonials = el)}>
            <div className="dashboard-section-header">
              <div className="dashboard-section-title">My Recommendations</div>
            </div>
            <TestimonialsTab profileId={profile?.id} isPremium={isPremium} />
          </div>

          {/* Surveys section */}
          <div className={`dashboard-section ${activeTab !== 'surveys' ? 'hidden' : ''}`} data-tab="surveys" ref={el => (sectionRefs.current.surveys = el)}>
            <div className="dashboard-section-header">
              <div className="dashboard-section-title">Survey Archive</div>
            </div>
            <SurveysTab userId={user?.id} isPremium={isPremium} />
          </div>

          {/* Billing section */}
          <div className={`dashboard-section ${activeTab !== 'billing' ? 'hidden' : ''}`} data-tab="billing" ref={el => (sectionRefs.current.billing = el)}>
            <div className="dashboard-section-header">
              <div className="dashboard-section-title">Billing & Subscription</div>
            </div>
            <BillingTab userId={user?.id} profile={profile} />
          </div>

          {/* Settings section */}
          <div className={`dashboard-section ${activeTab !== 'settings' ? 'hidden' : ''}`} data-tab="settings" ref={el => (sectionRefs.current.settings = el)}>
            <div className="dashboard-section-header">
              <div className="dashboard-section-title">Visibility & Contact Settings</div>
            </div>
            <SettingsTab
              profile={profile}
              isPremium={isPremium}
              isStaff={user?.is_staff}
              pii={pii}
              authEmail={authEmail}
              onSave={saveProfile}
              onSavePii={savePii}
            />
          </div>

          {/* Manage section — staff only */}
          {user?.is_staff && (
            <div className={`dashboard-section ${activeTab !== 'manage' ? 'hidden' : ''}`} data-tab="manage" ref={el => (sectionRefs.current.manage = el)}>
              <div className="dashboard-section-header">
                <div className="dashboard-section-title">Back Office Access</div>
              </div>
              <ManageTab onNavigate={onNavigate} />
            </div>
          )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
